import "@fortawesome/fontawesome-free/css/all.css";
import * as Sentry from "@sentry/browser";
import React, { FunctionComponent, memo } from "react";
import ReactDOM from "react-dom";
import config from "./config";
import "./index.css";

import { Provider as JotaiProvider } from "jotai";
import { Provider as ReduxProvider } from "react-redux";
import App from "./old/pages/App";

import "bootstrap/dist/css/bootstrap.css";
// import "./old/assets/scss/paper-dashboard.scss";
// import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
// import "./assets/scss/general-page-layout-fix.scss";
// AG Enterprise theme and license
import "ag-grid-enterprise/dist/styles/ag-grid.css";
import "ag-grid-enterprise/dist/styles/ag-theme-balham.css";

// import "./assets/scss/ag-theme-balham-overwrite.scss";
import DateFnsUtils from "@date-io/date-fns";
import { CssBaseline } from "@material-ui/core";
import {
  createTheme,
  StylesProvider,
  ThemeProvider as MuiThemeProvider,
} from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { LicenseManager } from "ag-grid-enterprise";
import "date-fns";
import { BrowserRouter as Router } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import ServiceWorkerUpdater from "./ServiceWorkerUpdater";
import { store } from "./store/store";
import { useTenantColorSettingValues } from "pages/configuration/configurationApi";

import {
  OpenSans,
  OpenSansBold,
  OpenSansBoldItalic,
  OpenSansExtraBold,
  OpenSansExtraBoldItalic,
  OpenSansItalic,
  OpenSansLight,
  OpenSansLightItalic,
  OpenSansMedium,
  OpenSansMediumItalic,
  OpenSansSemiBold,
  OpenSansSemiBoldItalic,
} from "./fonts";

if (config.env !== "development") {
  // Load Sentry  https://docs.sentry.io/platforms/javascript/react/
  Sentry.init({
    dsn: "https://54aac10797ae4e919c80a2805f6570af@o171488.ingest.sentry.io/1249861",
  });
}

// Set license key
LicenseManager.setLicenseKey(
  "CompanyName=Invenso BV,LicensedApplication=Cloudiadata BV,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=1,LicensedProductionInstancesCount=0,AssetReference=AG-033611,SupportServicesEnd=4_October_2023_[v2]_MTY5NjM3NDAwMDAwMA==578829b790e2ca6f5d3a2d8e67e7f6ad"
);

const AppWrapper: FunctionComponent = memo(() => {
  const colorSettings = useTenantColorSettingValues();

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main:
              colorSettings?.find((s) => s.name === "primary")?.color ||
              "#660099",
            contrastText:
              colorSettings?.find((s) => s.name === "primaryContrast")?.color ||
              "#fff",
          },
          secondary: {
            main:
              colorSettings?.find((s) => s.name === "secondary")?.color ||
              "#ff6600",
            contrastText:
              colorSettings?.find((s) => s.name === "secondaryContrast")
                ?.color || "#000",
          },
        },
        typography: {
          fontFamily: "OpenSans",
        },
        overrides: {
          MuiCssBaseline: {
            "@global": {
              "@font-face": [
                OpenSans,
                OpenSansBold,
                OpenSansBoldItalic,
                OpenSansExtraBold,
                OpenSansExtraBoldItalic,
                OpenSansItalic,
                OpenSansLight,
                OpenSansLightItalic,
                OpenSansMedium,
                OpenSansMediumItalic,
                OpenSansSemiBold,
                OpenSansSemiBoldItalic,
              ],
            },
          },
          MuiTypography: {
            h2: {
              fontWeight: 700,
              lineHeight: 1.5,
              fontSize: "1.5rem",
            },
            h3: {
              fontWeight: 600,
              lineHeight: 2,
              fontSize: "1.4rem",
            },
            h4: {
              fontWeight: 700,
              lineHeight: 1.5,
              fontSize: "1.5rem",
            },
            subtitle2: {
              textTransform: "none",
            },
          },
          MuiTableRow: {
            root: {
              "&.Mui-selected": {
                backgroundColor: "#b7e4ff",
              },
            },
          },
        },
      }),
    [colorSettings]
  );

  return (
    <MuiThemeProvider theme={theme}>
      <ServiceWorkerUpdater updateInterval={3600} env={config.env}>
        {/* check for updates every hour */}
        <ThemeProvider theme={theme}>
          <StylesProvider injectFirst>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <Router>
                <CssBaseline />
                <App />
              </Router>
            </MuiPickersUtilsProvider>
          </StylesProvider>
        </ThemeProvider>
      </ServiceWorkerUpdater>
    </MuiThemeProvider>
  );
});

ReactDOM.render(
  <React.StrictMode>
    <JotaiProvider>
      <ReduxProvider store={store}>
        <AppWrapper />
      </ReduxProvider>
    </JotaiProvider>
  </React.StrictMode>,
  document.getElementById("root")
);
